import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Progress } from '../components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { CheckCircle, Circle, FileText, Calendar, TestTube, Bell, ClipboardCheck } from 'lucide-react';
import { stateRequirements } from '../stateRequirements';
import { mockUser } from '../mock';
import { useToast } from '../hooks/use-toast';

const StateRequirementsPage = () => {
  const [selectedState, setSelectedState] = useState(mockUser.state);
  const [requirements, setRequirements] = useState([]);
  const { toast } = useToast();

  useEffect(() => {
    const stateReqs = stateRequirements[selectedState]?.requirements || [];
    setRequirements(stateReqs);
  }, [selectedState]);

  const handleToggle = (reqId) => {
    const updated = requirements.map(req =>
      req.id === reqId ? { ...req, completed: !req.completed } : req
    );
    setRequirements(updated);
  };

  const completedCount = requirements.filter(r => r.completed).length;
  const progress = requirements.length > 0 ? (completedCount / requirements.length) * 100 : 0;

  const categoryIcons = {
    attendance: Calendar,
    testing: TestTube,
    notification: Bell,
    portfolio: FileText,
    reporting: ClipboardCheck,
    records: FileText,
    health: CheckCircle,
    qualification: CheckCircle,
    instruction: CheckCircle,
    regulation: CheckCircle
  };

  const categoryColors = {
    attendance: 'bg-blue-100 text-blue-700',
    testing: 'bg-purple-100 text-purple-700',
    notification: 'bg-amber-100 text-amber-700',
    portfolio: 'bg-green-100 text-green-700',
    reporting: 'bg-pink-100 text-pink-700',
    records: 'bg-teal-100 text-teal-700',
    health: 'bg-red-100 text-red-700',
    qualification: 'bg-indigo-100 text-indigo-700',
    instruction: 'bg-cyan-100 text-cyan-700',
    regulation: 'bg-slate-100 text-slate-700'
  };

  const groupedRequirements = requirements.reduce((acc, req) => {
    if (!acc[req.category]) acc[req.category] = [];
    acc[req.category].push(req);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50">
      <div className="max-w-5xl mx-auto px-6 py-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-slate-900 mb-2">State Requirements</h1>
          <p className="text-slate-600 text-lg">Track your compliance checklist</p>
        </div>

        {/* State Selector */}
        <Card className="p-6 mb-8 border-0 shadow-lg bg-white">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-bold text-slate-900">Select State</h2>
              <p className="text-sm text-slate-600">View requirements for any state</p>
            </div>
            <Select value={selectedState} onValueChange={setSelectedState}>
              <SelectTrigger className="w-64">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="max-h-60">
                {Object.keys(stateRequirements).sort().map(state => (
                  <SelectItem key={state} value={state}>
                    {stateRequirements[state].name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Progress Bar */}
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-600">Completion Progress</span>
              <span className="font-semibold text-green-700">
                {completedCount} of {requirements.length} completed
              </span>
            </div>
            <Progress value={progress} className="h-3" />
          </div>
        </Card>

        {/* Requirements by Category */}
        <div className="space-y-6">
          {Object.entries(groupedRequirements).map(([category, reqs]) => {
            const Icon = categoryIcons[category] || Circle;
            const colorClass = categoryColors[category] || 'bg-slate-100 text-slate-700';

            return (
              <Card key={category} className="p-6 border-0 shadow-lg bg-white">
                <div className="flex items-center gap-3 mb-4">
                  <div className={`p-2 rounded-lg ${colorClass}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 capitalize">
                    {category.replace('_', ' ')}
                  </h3>
                </div>

                <div className="space-y-3">
                  {reqs.map(req => (
                    <div
                      key={req.id}
                      className="flex items-start gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer"
                      onClick={() => handleToggle(req.id)}
                    >
                      <Checkbox
                        checked={req.completed}
                        onCheckedChange={() => handleToggle(req.id)}
                        className="mt-1"
                      />
                      <span className={`flex-1 ${
                        req.completed ? 'line-through text-slate-500' : 'text-slate-700'
                      }`}>
                        {req.title}
                      </span>
                      {req.completed && (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      )}
                    </div>
                  ))}
                </div>
              </Card>
            );
          })}
        </div>

        {requirements.length === 0 && (
          <Card className="p-12 border-2 border-dashed border-slate-200 bg-slate-50 text-center">
            <p className="text-slate-500 text-lg">No requirements data available for this state.</p>
          </Card>
        )}

        {/* Info Card */}
        <Card className="p-6 mt-8 border-0 shadow-lg bg-gradient-to-br from-blue-50 to-teal-50">
          <h3 className="font-bold text-slate-900 mb-2">Important Notes</h3>
          <ul className="text-sm text-slate-700 space-y-1">
            <li>• Requirements are updated as of 2025 - always verify with your state's education department</li>
            <li>• This checklist resets annually for tracking each school year</li>
            <li>• You can add custom requirements using the form below</li>
            <li>• Keep supporting documentation even if not explicitly required</li>
          </ul>
        </Card>
      </div>
    </div>
  );
};

export default StateRequirementsPage;